/**
 * Topbar desktop — la couche de navigation qui manquait au-dessus du contenu :
 * fil d'Ariane (registre de routes), raccourci ⌘K vers la palette, badge
 * d'environnement hors production et société de l'utilisateur. Masquée en
 * mobile (le header burger existant assume ce rôle).
 */
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Search } from 'lucide-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { buildBreadcrumbs } from '@/lib/route-registry';

export const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

export function Topbar({ onOpenPalette }: { onOpenPalette: () => void }) {
  const { t } = useTranslation();
  const location = useLocation();
  const { user } = useAuthContext();

  const crumbs = buildBreadcrumbs(location.pathname);
  const mode = import.meta.env.MODE;
  const showEnvBadge = mode !== 'production';
  const companyName = user.company?.name;

  return (
      <header className="hidden h-11 shrink-0 items-center gap-3 border-b border-border bg-surface px-4 lg:flex lg:px-6">
        {/* Fil d'Ariane */}
        <nav aria-label={t('nav.breadcrumb')} className="min-w-0 flex-1">
          <ol className="flex items-center gap-1 text-[13px]">
            {crumbs.map((c, i) => {
              const last = i === crumbs.length - 1;
              return (
                <li key={c.path} className="flex min-w-0 items-center gap-1">
                  {i > 0 && <ChevronRight className="h-3.5 w-3.5 shrink-0 text-subtle" aria-hidden="true" />}
                  {last ? (
                    <span aria-current="page" className="truncate font-medium text-text">
                      {t(c.labelKey)}
                    </span>
                  ) : (
                    <Link
                      to={c.path.includes(':') ? location.pathname : c.path}
                      className="truncate text-muted transition-colors hover:text-text"
                    >
                      {t(c.labelKey)}
                    </Link>
                  )}
                </li>
              );
            })}
          </ol>
        </nav>

        {/* Contexte : environnement + société */}
        {showEnvBadge && (
          <span className="rounded bg-warning-soft px-2 py-0.5 text-2xs font-semibold uppercase tracking-wide text-warning">
            {mode}
          </span>
        )}
        {companyName && (
          <span className="hidden max-w-[200px] truncate text-2xs text-subtle xl:block" title={companyName}>
            {companyName}
          </span>
        )}

        {/* Palette ⌘K */}
        <button
          type="button"
          onClick={onOpenPalette}
          className="flex items-center gap-2 rounded border border-border bg-surface-subtle px-2.5 py-1.5 text-2xs text-subtle transition-colors hover:border-accent/40 hover:text-muted"
          aria-label={t('palette.openAria')}
        >
          <Search className="h-3.5 w-3.5" aria-hidden="true" />
          <span>{t('palette.goTo')}</span>
          <kbd className="rounded border border-border bg-surface px-1 py-px font-sans text-2xs">
            {IS_MAC ? '⌘K' : 'Ctrl K'}
          </kbd>
        </button>
      </header>
  );
}
